import React, { useCallback, useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import {
  FaArrowLeft,
  FaSearch,
  FaSpinner,
  FaPlay,
  FaCheck,
  FaFileExcel,
  FaChartBar,
  FaTimes,
  FaUsers,
  FaBuilding,
} from "react-icons/fa";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useAuth from "../../hooks/useAuthContext";
import api from "../../services/axios";
import exportToExcel from "../../services/exportExcel";
import "./admPatrocinadoresList.css";
import "./admGerarAgendamento.css";

const PLANOS_FILTRO = [
  { value: "", label: "Todos os planos" },
  { value: "3", label: "Advance" },
  { value: "4", label: "Lite" },
  { value: "6", label: "Lite M" },
];

function normalizarTexto(texto) {
  return String(texto || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function celulaLabel(celula) {
  if (!celula) return "";
  const label = celula.label || "";
  if (celula.tipo === "reuniao") {
    return String(label).replace(/^Reunião com\s+/i, "");
  }
  if (celula.tipo === "m_m") {
    const semPrefixo = String(label).replace(/^M&M\s+com\s+/i, "");
    return semPrefixo || "M&M";
  }
  return label;
}

function celulaTitle(celula) {
  if (!celula) return "";
  return celula.label || celulaLabel(celula);
}

function celulaClasse(celula) {
  if (!celula) return "";
  if (celula.tipo === "m_m") return "aga-cell--mm";
  if (celula.tipo === "caviar") return "aga-cell--caviar";
  if (celula.tipo === "reuniao") return "aga-cell--reuniao";
  if (celula.tipo === "conteudo") return "aga-cell--conteudo";
  if (celula.tipo === "bloqueado") return "aga-cell--bloqueado";
  return "aga-cell--outro";
}

function celulaEhMarcacao(celula) {
  return celula?.tipo === "reuniao" || celula?.tipo === "m_m";
}

function SlotTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const item = payload[0]?.payload || {};
  return (
    <div className="aga-chart-tooltip">
      <strong>{item.label}</strong>
      <div>
        Reuniões: <strong>{item.quantidade ?? 0}</strong>
      </div>
    </div>
  );
}

function pctLista(marcadas, total) {
  const den = Number(total) || 0;
  if (den <= 0) return 0;
  return Math.round(((Number(marcadas) || 0) / den) * 100);
}

const AdmGerarAgendamento = () => {
  const navigate = useNavigate();
  const { usuario } = useAuth();

  useEffect(() => {
    if (usuario && usuario.tipo !== "administracao") {
      navigate("/private/adm");
    }
  }, [usuario, navigate]);

  const [dados, setDados] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isConfirmando, setIsConfirmando] = useState(false);
  const [error, setError] = useState(null);
  const [analiseModal, setAnaliseModal] = useState(null);
  const [modalCaviarSemSlot, setModalCaviarSemSlot] = useState(false);
  const [modalMatchMeetSemSlot, setModalMatchMeetSemSlot] = useState(false);
  const [modalConfirmar, setModalConfirmar] = useState(false);

  const [busca, setBusca] = useState("");
  const [filtroPlano, setFiltroPlano] = useState("");
  const [filtroPatrocinador, setFiltroPatrocinador] = useState("");
  const [somenteComReuniao, setSomenteComReuniao] = useState(false);
  const [somenteSemReuniao, setSomenteSemReuniao] = useState(false);
  const [somenteMatchMeet, setSomenteMatchMeet] = useState(false);

  const slots = dados?.slots || [];
  const resumo = dados?.resumo || null;

  const opcoesPatrocinador = useMemo(() => {
    const lista = resumo?.patrocinadores || [];
    return [...lista].sort((a, b) =>
      String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR"),
    );
  }, [resumo]);

  const patrocinadorSelecionado = useMemo(() => {
    if (!filtroPatrocinador) return null;
    return (
      opcoesPatrocinador.find(
        (p) => String(p.id_patrocinador) === String(filtroPatrocinador),
      ) || null
    );
  }, [filtroPatrocinador, opcoesPatrocinador]);

  const reunioesPorConvidado = useMemo(() => {
    const map = new Map();
    for (const r of dados?.reunioes || []) {
      const lista = map.get(r.idConvidado) || [];
      lista.push(r);
      map.set(r.idConvidado, lista);
    }
    return map;
  }, [dados]);

  const matchMeetPorConvidado = useMemo(() => {
    const map = new Map();
    for (const r of dados?.match_meet || []) {
      const set = map.get(r.idConvidado) || new Set();
      set.add(r.idSlot);
      map.set(r.idConvidado, set);
    }
    return map;
  }, [dados]);

  const slotsPatrocinadorPorConvidado = useMemo(() => {
    const map = new Map();
    if (!filtroPatrocinador) return map;
    const idPatro = Number(filtroPatrocinador);
    for (const r of [...(dados?.reunioes || []), ...(dados?.match_meet || [])]) {
      if (r.idPatrocinador !== idPatro) continue;
      const set = map.get(r.idConvidado) || new Set();
      set.add(r.idSlot);
      map.set(r.idConvidado, set);
    }
    return map;
  }, [dados, filtroPatrocinador]);

  const matrizFiltrada = useMemo(() => {
    const linhas = dados?.matriz || [];
    const termo = normalizarTexto(busca.trim());
    const nomePatro = patrocinadorSelecionado?.nome || "";

    return linhas.filter((linha) => {
      if (termo && !normalizarTexto(linha.nome).includes(termo)) {
        return false;
      }

      const reunioesConvidado = reunioesPorConvidado.get(linha.id_usuario) || [];
      const slotsMm = matchMeetPorConvidado.get(linha.id_usuario);

      if (somenteMatchMeet) {
        if (!slotsMm || slotsMm.size === 0) return false;
      }

      if (filtroPlano) {
        const temPlano = reunioesConvidado.some(
          (r) => String(r.idPlano) === filtroPlano,
        );
        const temMmPlano = (dados?.match_meet || []).some(
          (r) =>
            r.idConvidado === linha.id_usuario &&
            String(r.idPlano) === filtroPlano,
        );
        if (!temPlano && !temMmPlano) return false;
      }

      if (filtroPatrocinador) {
        const idPatro = Number(filtroPatrocinador);
        const temProposta = reunioesConvidado.some(
          (r) => r.idPatrocinador === idPatro,
        );
        const temMm = (dados?.match_meet || []).some(
          (r) =>
            r.idConvidado === linha.id_usuario &&
            r.idPatrocinador === idPatro,
        );
        const temLabel =
          !!nomePatro &&
          Object.values(linha.slots || {}).some(
            (c) =>
              celulaEhMarcacao(c) &&
              String(c.label || "").includes(nomePatro),
          );
        if (!temProposta && !temMm && !temLabel) return false;
      }

      if (somenteComReuniao) {
        const temReuniao = Object.values(linha.slots || {}).some(celulaEhMarcacao);
        if (!temReuniao) return false;
      }

      if (somenteSemReuniao) {
        const temReuniao = Object.values(linha.slots || {}).some(celulaEhMarcacao);
        if (temReuniao) return false;
      }

      return true;
    });
  }, [
    dados,
    busca,
    filtroPlano,
    filtroPatrocinador,
    patrocinadorSelecionado,
    somenteComReuniao,
    somenteSemReuniao,
    somenteMatchMeet,
    reunioesPorConvidado,
    matchMeetPorConvidado,
  ]);

  const celulaParaExibicao = useCallback(
    (linha, idSlot) => {
      const celula = linha.slots?.[`slot_${idSlot}`] || null;
      // Com "Somente M&M", mostra a agenda completa do convidado filtrado.
      if (somenteMatchMeet || !filtroPatrocinador || !celula) return celula;

      if (celula.tipo !== "reuniao" && celula.tipo !== "m_m") return celula;

      const slotsDoPatro = slotsPatrocinadorPorConvidado.get(linha.id_usuario);
      if (slotsDoPatro?.has(idSlot)) return celula;

      const nomePatro = patrocinadorSelecionado?.nome || "";
      if (nomePatro && String(celula.label || "").includes(nomePatro)) {
        return celula;
      }

      return null;
    },
    [
      filtroPatrocinador,
      slotsPatrocinadorPorConvidado,
      patrocinadorSelecionado,
      somenteMatchMeet,
    ],
  );

  const dadosGraficoSlots = useMemo(() => {
    if (!slots.length) return [];

    const contagem = new Map();
    for (const linha of matrizFiltrada) {
      for (const s of slots) {
        const celula = celulaParaExibicao(linha, s.id);
        if (!celula) continue;
        if (somenteMatchMeet) {
          if (celula.tipo !== "m_m") continue;
        } else if (!celulaEhMarcacao(celula)) {
          continue;
        }
        contagem.set(s.id, (contagem.get(s.id) || 0) + 1);
      }
    }

    return slots.map((s) => {
      const hora = String(s.horario_inicio || "").substring(0, 5);
      return {
        id: s.id,
        label: `Slot ${s.id}${hora ? ` · ${hora}` : ""}`,
        quantidade: contagem.get(s.id) || 0,
      };
    });
  }, [slots, matrizFiltrada, celulaParaExibicao, somenteMatchMeet]);

  const rankingConvidados = useMemo(() => {
    const map = new Map();
    for (const linha of matrizFiltrada) {
      let total = 0;
      for (const s of slots) {
        const celula = celulaParaExibicao(linha, s.id);
        if (!celula) continue;
        if (somenteMatchMeet) {
          if (celula.tipo !== "m_m") continue;
        } else if (!celulaEhMarcacao(celula)) {
          continue;
        }
        total += 1;
      }
      if (total <= 0) continue;
      map.set(linha.id_usuario, {
        id: linha.id_usuario,
        nome: linha.nome || `#${linha.id_usuario}`,
        total,
        slots: linha.slots,
      });
    }

    return [...map.values()]
      .sort(
        (a, b) =>
          b.total - a.total ||
          String(a.nome).localeCompare(String(b.nome), "pt-BR"),
      )
      .slice(0, 15);
  }, [matrizFiltrada, slots, celulaParaExibicao, somenteMatchMeet]);

  const indicadoresListasPatro = useMemo(() => {
    if (!patrocinadorSelecionado) return null;
    const prio = patrocinadorSelecionado.lista_prioritaria || {};
    const dese = patrocinadorSelecionado.lista_desejavel || {};
    // Denominador = cota de escolha do plano (escolha_maxima_mais_quero / escolha_maxima_quero)
    const cotaPrio = Number(prio.total) || 0;
    const cotaDese = Number(dese.total) || 0;
    return {
      prioritarios: {
        total: cotaPrio,
        marcadas: Number(prio.marcadas) || 0,
        pct: pctLista(prio.marcadas, cotaPrio),
      },
      desejaveis: {
        total: cotaDese,
        marcadas: Number(dese.marcadas) || 0,
        pct: pctLista(dese.marcadas, cotaDese),
      },
    };
  }, [patrocinadorSelecionado]);

  const utilizacaoListasPatrocinadores = useMemo(() => {
    const lista = resumo?.patrocinadores || [];
    return [...lista]
      .map((p) => {
        const prio = p.lista_prioritaria || {};
        const dese = p.lista_desejavel || {};
        // Mesma base do indicador: marcações de reunião (sem M&M)
        const totalMarcacoes = Number(p.total) || 0;
        const prioMarcadas = Number(prio.marcadas) || 0;
        const deseMarcadas = Number(dese.marcadas) || 0;
        return {
          id: p.id_patrocinador,
          nome: p.nome || `#${p.id_patrocinador}`,
          sequencia: p.sequencia ?? null,
          nomePlano: p.nome_plano || "",
          totalMarcacoes,
          prioritarios: {
            total: Number(prio.total) || 0,
            marcadas: prioMarcadas,
            pct: pctLista(prioMarcadas, totalMarcacoes),
          },
          desejaveis: {
            total: Number(dese.total) || 0,
            marcadas: deseMarcadas,
            pct: pctLista(deseMarcadas, totalMarcacoes),
          },
        };
      })
      .sort(
        (a, b) =>
          (a.sequencia ?? Number.MAX_SAFE_INTEGER) -
            (b.sequencia ?? Number.MAX_SAFE_INTEGER) ||
          String(a.nome).localeCompare(String(b.nome), "pt-BR"),
      );
  }, [resumo]);

  const abrirAnalise = useCallback(
    (linha) => {
      const analise =
        dados?.analise_convidados?.[String(linha.id_usuario)] || null;
      setAnaliseModal({
        tipo: "convidado",
        id: linha.id_usuario,
        nome: linha.nome,
        slots: linha.slots,
        analise,
      });
    },
    [dados],
  );

  const abrirAnalisePatrocinador = useCallback(() => {
    if (!patrocinadorSelecionado) return;
    const id = patrocinadorSelecionado.id_patrocinador;
    const analise =
      dados?.analise_patrocinadores?.[String(id)] || null;
    setAnaliseModal({
      tipo: "patrocinador",
      id,
      nome: patrocinadorSelecionado.nome || analise?.nome || "Patrocinador",
      slots: analise?.slots || {},
      analise: analise
        ? {
            reunioes_finais: analise.reunioes_finais,
            cota: analise.cota,
            eventos: analise.eventos || [],
          }
        : null,
    });
  }, [dados, patrocinadorSelecionado]);

  const gerarPreview = async () => {
    setIsLoadingPreview(true);
    setError(null);
    try {
      const resp = await api.post("/agenda-gerar-preview");
      if (!resp.data?.ok) {
        throw new Error(resp.data?.mensagem || "Falha ao gerar preview");
      }
      setDados(resp.data.dados);
      toast.success(
        `Preview gerado: ${resp.data.dados?.resumo?.total_propostas || 0} reunião(ões), ${resp.data.dados?.resumo?.total_caviar || 0} caviar e ${resp.data.dados?.resumo?.total_match_meet || 0} Match & Meet.`,
      );
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.mensagem ||
        err?.message ||
        "Erro ao gerar preview do agendamento.";
      setError(msg);
      toast.error(msg);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const abrirModalConfirmar = () => {
    if (!dados || isConfirmando || isLoadingPreview) return;
    setModalConfirmar(true);
  };

  const confirmarAgendamento = async () => {
    if (!dados) return;
    setModalConfirmar(false);
    setIsConfirmando(true);
    setError(null);
    try {
      const resp = await api.post("/agenda-gerar-confirmar");
      if (!resp.data?.ok) {
        throw new Error(resp.data?.mensagem || "Falha ao confirmar");
      }
      setDados(resp.data.dados);
      toast.success(
        resp.data.mensagem ||
          `${resp.data.dados?.criadas || 0} reunião(ões) criada(s).`,
      );
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.mensagem ||
        err?.message ||
        "Erro ao confirmar o agendamento.";
      setError(msg);
      toast.error(msg);
    } finally {
      setIsConfirmando(false);
    }
  };

  const exportarExcel = useCallback(() => {
    if (!matrizFiltrada.length || !slots.length) {
      toast.info("Gere o preview antes de exportar.");
      return;
    }

    const linhas = matrizFiltrada.map((linha) => {
      const row = {
        Participante: linha.nome || "",
      };
      for (const s of slots) {
        const hora = String(s.horario_inicio || "").substring(0, 5);
        const col = `Slot ${s.id}${hora ? ` (${hora})` : ""}`;
        row[col] = celulaLabel(celulaParaExibicao(linha, s.id));
      }
      return row;
    });

    const dataRef = new Date().toISOString().slice(0, 10);
    const sufixo = patrocinadorSelecionado
      ? `_${String(patrocinadorSelecionado.nome || "patrocinador")
          .replace(/[^\w\-]+/g, "_")
          .slice(0, 40)}`
      : "";
    exportToExcel(linhas, `preview_agendamento${sufixo}_${dataRef}`);
    toast.success("Excel exportado.");
  }, [matrizFiltrada, slots, celulaParaExibicao, patrocinadorSelecionado]);

  const porPlano = resumo?.por_plano
    ? Object.values(resumo.por_plano)
    : [];

  if (usuario?.tipo !== "administracao") {
    return null;
  }

  return (
    <div className="container-adm-main">
      <div className="apl-page apl-page--wide-form">
        <header className="apl-header">
          <div className="apl-header-text">
            <h1>Gerar agendamento</h1>
            <p>
              Preview e confirmação do matching automático (Advance, Lite e Lite
              M).
            </p>
          </div>
          <div className="apl-toolbar">
            <button
              type="button"
              className="apl-btn apl-btn--primary"
              onClick={gerarPreview}
              disabled={isLoadingPreview || isConfirmando}
            >
              {isLoadingPreview ? (
                <FaSpinner className="aga-spin" />
              ) : (
                <FaPlay />
              )}
              Gerar preview
            </button>
            <button
              type="button"
              className="apl-btn apl-btn--ghost"
              onClick={exportarExcel}
              disabled={!dados || matrizFiltrada.length === 0 || isLoadingPreview}
              title="Exportar tabela de preview em Excel"
            >
              <FaFileExcel />
              Exportar Excel
            </button>
            <button
              type="button"
              className="apl-btn apl-btn--ghost"
              onClick={abrirModalConfirmar}
              disabled={!dados || isConfirmando || isLoadingPreview}
            >
              {isConfirmando ? (
                <FaSpinner className="aga-spin" />
              ) : (
                <FaCheck />
              )}
              Confirmar agendamento
            </button>
            <button
              type="button"
              className="apl-btn apl-btn--ghost"
              onClick={() => navigate("/private/adm")}
            >
              <FaArrowLeft /> Voltar
            </button>
          </div>
        </header>

        <div className="body-tela-adm">
          {error && <div className="apl-alert">{error}</div>}

          {resumo && (
            <section className="apl-metrics">
              <div className="apl-metric">
                <span className="apl-metric-label">Propostas</span>
                <span className="apl-metric-value">
                  {resumo.total_propostas}
                </span>
              </div>
              <button
                type="button"
                className="apl-metric aga-metric-btn is-clickable"
                onClick={() => setModalCaviarSemSlot(true)}
                title="Ver interessados sem slot disponível"
              >
                <span className="apl-metric-label">Caviar</span>
                <span className="apl-metric-value">
                  {resumo.total_caviar || 0}
                  <span className="aga-metric-denominador">
                    {" "}
                    / {resumo.total_interessados_caviar || 0}
                  </span>
                </span>
                <span className="apl-metric-sub">
                  agendados / interessados
                  {(resumo.caviar_sem_slot || []).length
                    ? ` · ${(resumo.caviar_sem_slot || []).length} sem slot`
                    : ""}
                  {Object.values(resumo.caviar_por_slot || {}).length
                    ? ` · ${Object.values(resumo.caviar_por_slot || {})
                        .map((s) => `Slot ${s.id_slot}: ${s.vagas}/50`)
                        .join(" · ")}`
                    : ""}
                </span>
              </button>
              <button
                type="button"
                className="apl-metric aga-metric-btn is-clickable"
                onClick={() => setModalMatchMeetSemSlot(true)}
                title="Ver interessados Match & Meet sem alocação"
              >
                <span className="apl-metric-label">Match & Meet</span>
                <span className="apl-metric-value">
                  {resumo.total_match_meet || 0}
                  <span className="aga-metric-denominador">
                    {" "}
                    / {resumo.total_interessados_match_meet || 0}
                  </span>
                </span>
                <span className="apl-metric-sub">
                  20 no slot 10
                  {Object.values(resumo.match_meet_por_slot || {}).length
                    ? ` · ${Object.values(resumo.match_meet_por_slot || {})
                        .map((s) => `Slot ${s.id_slot}: ${s.propostas}/${s.limite}`)
                        .join(" · ")}`
                    : ""}
                  {(resumo.match_meet_sem_slot || []).length
                    ? ` · ${(resumo.match_meet_sem_slot || []).length} sem alocação`
                    : ""}
                </span>
              </button>
              {porPlano.map((p) => (
                <div key={p.id_plano} className="apl-metric">
                  <span className="apl-metric-label">{p.nome}</span>
                  <span className="apl-metric-value">{p.propostas}</span>
                  <span className="apl-metric-sub">
                    {p.cotas_incompletas} cota(s) incompleta(s)
                  </span>
                </div>
              ))}
            </section>
          )}

          {resumo?.avisos?.length > 0 && (
            <section className="aga-avisos">
              <h2>Avisos</h2>
              <ul>
                {resumo.avisos.map((a, i) => (
                  <li key={i}>{a}</li>
                ))}
              </ul>
            </section>
          )}

          <section className="apl-card">
            <div className="apl-toolbar aga-filters">
              <div className="apl-search">
                <FaSearch className="apl-search-icon" aria-hidden />
                <input
                  className="apl-search-input"
                  type="search"
                  placeholder="Buscar participante…"
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                />
              </div>
              <label className="apl-filter-pill apl-filter-pill--select">
                <span>Plano</span>
                <select
                  className="apl-filter-select"
                  value={filtroPlano}
                  onChange={(e) => setFiltroPlano(e.target.value)}
                >
                  {PLANOS_FILTRO.map((o) => (
                    <option key={o.value || "all"} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="apl-filter-pill apl-filter-pill--select aga-filter-patrocinador">
                <span>Patrocinador</span>
                <select
                  className="apl-filter-select"
                  value={filtroPatrocinador}
                  onChange={(e) => setFiltroPatrocinador(e.target.value)}
                  disabled={!dados}
                >
                  <option value="">Todos os patrocinadores</option>
                  {opcoesPatrocinador.map((p) => (
                    <option
                      key={p.id_patrocinador}
                      value={String(p.id_patrocinador)}
                    >
                      {p.nome}
                      {p.marcadas_propostas
                        ? ` (${p.marcadas_propostas})`
                        : ""}
                    </option>
                  ))}
                </select>
              </label>
              <label className="apl-filter-pill">
                <input
                  type="checkbox"
                  checked={somenteComReuniao}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setSomenteComReuniao(checked);
                    if (checked) setSomenteSemReuniao(false);
                  }}
                />
                Somente com reunião
              </label>
              <label className="apl-filter-pill">
                <input
                  type="checkbox"
                  checked={somenteSemReuniao}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setSomenteSemReuniao(checked);
                    if (checked) {
                      setSomenteComReuniao(false);
                      setSomenteMatchMeet(false);
                    }
                  }}
                />
                Sem reuniões
              </label>
              <label className="apl-filter-pill">
                <input
                  type="checkbox"
                  checked={somenteMatchMeet}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setSomenteMatchMeet(checked);
                    if (checked) setSomenteSemReuniao(false);
                  }}
                />
                M&amp;M
              </label>
            </div>

            {!dados && !isLoadingPreview && (
              <div className="apl-empty">
                Clique em <strong>Gerar preview</strong> para visualizar a
                matriz de agendamentos.
              </div>
            )}

            {isLoadingPreview && (
              <div className="apl-loading">
                <FaSpinner className="aga-spin" /> Gerando preview…
              </div>
            )}

            {dados && !isLoadingPreview && (
              <div className="apl-table-wrap aga-table-wrap">
                <table className="apl-table aga-table">
                  <thead>
                    <tr>
                      <th className="aga-th-participante">Participante</th>
                      {slots.map((s) => (
                        <th key={s.id} title={`${s.data} ${s.horario_inicio}`}>
                          Slot {s.id}
                          <span className="aga-slot-hora">
                            {String(s.horario_inicio || "").substring(0, 5)}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {matrizFiltrada.length === 0 ? (
                      <tr>
                        <td colSpan={slots.length + 1}>
                          <div className="apl-empty">
                            Nenhum participante com os filtros atuais.
                          </div>
                        </td>
                      </tr>
                    ) : (
                      matrizFiltrada.map((linha) => (
                        <tr key={linha.id_usuario}>
                          <td className="aga-td-participante">
                            <button
                              type="button"
                              className="aga-nome aga-nome--btn"
                              onClick={() => abrirAnalise(linha)}
                              title="Ver memória do agendamento"
                            >
                              {linha.nome}
                            </button>
                          </td>
                          {slots.map((s) => {
                            const celula = celulaParaExibicao(linha, s.id);
                            return (
                              <td
                                key={s.id}
                                className={`aga-cell ${celulaClasse(celula)}`}
                                title={celulaTitle(celula)}
                              >
                                {celulaLabel(celula)}
                              </td>
                            );
                          })}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {dados && !isLoadingPreview && indicadoresListasPatro && (
            <section
              className="apl-metrics aga-listas-metrics"
              aria-label={`Indicadores das listas de ${patrocinadorSelecionado?.nome || "patrocinador"}`}
            >
              <div className="apl-metric">
                <span className="apl-metric-label">Lista prioritária</span>
                <span className="apl-metric-value">
                  {indicadoresListasPatro.prioritarios.pct}%
                </span>
                <span className="apl-metric-sub">
                  {indicadoresListasPatro.prioritarios.marcadas} de{" "}
                  {indicadoresListasPatro.prioritarios.total} escolhas do plano
                  {patrocinadorSelecionado?.nome
                    ? ` · ${patrocinadorSelecionado.nome}`
                    : ""}
                </span>
              </div>
              <div className="apl-metric">
                <span className="apl-metric-label">Lista desejável</span>
                <span className="apl-metric-value">
                  {indicadoresListasPatro.desejaveis.pct}%
                </span>
                <span className="apl-metric-sub">
                  {indicadoresListasPatro.desejaveis.marcadas} de{" "}
                  {indicadoresListasPatro.desejaveis.total} escolhas do plano
                  {patrocinadorSelecionado?.nome
                    ? ` · ${patrocinadorSelecionado.nome}`
                    : ""}
                </span>
              </div>
              <button
                type="button"
                className="apl-metric aga-metric-btn is-clickable aga-listas-memoria-btn"
                onClick={abrirAnalisePatrocinador}
                title="Ver memória do agendamento do patrocinador"
              >
                <span className="apl-metric-label">Memória</span>
                <span className="apl-metric-value aga-listas-memoria-value">
                  Ver passos
                </span>
                <span className="apl-metric-sub">
                  montagem da agenda
                  {patrocinadorSelecionado?.nome
                    ? ` · ${patrocinadorSelecionado.nome}`
                    : ""}
                </span>
              </button>
            </section>
          )}

          {dados && !isLoadingPreview && (
            <div className="aga-bottom-grid">
              <section className="apl-card aga-chart-card">
                <div className="aga-chart-header">
                  <h2>
                    <FaChartBar aria-hidden />
                    Reuniões por slot
                  </h2>
                  <p>
                    Quantidade de marcações no preview
                    {somenteMatchMeet ? " de Match & Meet" : ""}
                    {patrocinadorSelecionado
                      ? ` para ${patrocinadorSelecionado.nome}`
                      : ""}
                    {filtroPlano
                      ? ` (${PLANOS_FILTRO.find((p) => p.value === filtroPlano)?.label || "plano"})`
                      : ""}
                    .
                  </p>
                </div>
                <div
                  className="aga-chart-wrap"
                  role="img"
                  aria-label="Gráfico de reuniões por slot"
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={dadosGraficoSlots}
                      margin={{ top: 8, right: 8, left: 0, bottom: 48 }}
                      barCategoryGap="18%"
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="#e2e8f0"
                      />
                      <XAxis
                        dataKey="label"
                        interval={0}
                        angle={-28}
                        textAnchor="end"
                        height={60}
                        tick={{ fill: "#64748b", fontSize: 11, fontWeight: 600 }}
                        axisLine={{ stroke: "#e2e8f0" }}
                        tickLine={false}
                      />
                      <YAxis
                        allowDecimals={false}
                        tick={{ fill: "#94a3b8", fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                        width={36}
                      />
                      <Tooltip
                        content={<SlotTooltip />}
                        cursor={{ fill: "rgba(13, 29, 65, 0.04)" }}
                      />
                      <Bar
                        dataKey="quantidade"
                        radius={[6, 6, 0, 0]}
                        maxBarSize={40}
                        fill="#0d1d41"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </section>

              <section className="apl-card aga-ranking-card">
                <div className="aga-chart-header">
                  <h2>
                    <FaUsers aria-hidden />
                    Ranking de convidados
                  </h2>
                  <p>
                    {somenteMatchMeet
                      ? "Convidados com Match & Meet no preview."
                      : "Convidados com mais marcações no preview."}
                  </p>
                </div>
                {rankingConvidados.length === 0 ? (
                  <div className="apl-empty">Sem marcações para ranquear.</div>
                ) : (
                  <ol className="aga-ranking-list">
                    {rankingConvidados.map((item, index) => {
                      return (
                        <li key={item.id} className="aga-ranking-item">
                          <span
                            className={`aga-rank-num${index < 3 ? " is-top" : ""}`}
                          >
                            {index + 1}
                          </span>
                          <button
                            type="button"
                            className="aga-rank-nome"
                            onClick={() =>
                              abrirAnalise({
                                id_usuario: item.id,
                                nome: item.nome,
                                slots: item.slots || {},
                              })
                            }
                            title="Ver memória do agendamento"
                          >
                            {item.nome}
                          </button>
                          <span className="aga-rank-count" title="Marcações">
                            {item.total}
                          </span>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </section>
            </div>
          )}

          {dados && !isLoadingPreview && (
            <section className="apl-card aga-utilizacao-card">
              <div className="aga-chart-header">
                <h2>
                  <FaBuilding aria-hidden />
                  Utilização das listas por patrocinador
                </h2>
                <p>
                  Quanto % das marcações de reunião do patrocinador vieram de
                  cada lista (M&amp;M não entra na conta), ordenado pela
                  sequência do matching.
                </p>
              </div>
              {utilizacaoListasPatrocinadores.length === 0 ? (
                <div className="apl-empty">
                  Nenhum patrocinador no preview.
                </div>
              ) : (
                <div className="aga-utilizacao-table-wrap">
                  <table className="aga-utilizacao-table">
                    <thead>
                      <tr>
                        <th scope="col">Seq.</th>
                        <th scope="col">Patrocinador</th>
                        <th scope="col">Plano</th>
                        <th scope="col">Prioritários</th>
                        <th scope="col">Desejáveis</th>
                      </tr>
                    </thead>
                    <tbody>
                      {utilizacaoListasPatrocinadores.map((item) => (
                        <tr key={item.id}>
                          <td className="aga-utilizacao-seq">
                            {item.sequencia != null ? item.sequencia : "—"}
                          </td>
                          <td className="aga-utilizacao-nome" title={item.nome}>
                            {item.nome}
                          </td>
                          <td className="aga-utilizacao-plano">
                            {item.nomePlano || "—"}
                          </td>
                          <td>
                            <div className="aga-utilizacao-pct">
                              <strong>{item.prioritarios.pct}%</strong>
                              <span>
                                {item.prioritarios.marcadas}/
                                {item.totalMarcacoes}
                              </span>
                            </div>
                            <div
                              className="aga-utilizacao-bar"
                              aria-hidden
                            >
                              <span
                                className="aga-utilizacao-bar-fill aga-utilizacao-bar-fill--prio"
                                style={{
                                  width: `${Math.min(100, item.prioritarios.pct)}%`,
                                }}
                              />
                            </div>
                          </td>
                          <td>
                            <div className="aga-utilizacao-pct">
                              <strong>{item.desejaveis.pct}%</strong>
                              <span>
                                {item.desejaveis.marcadas}/
                                {item.totalMarcacoes}
                              </span>
                            </div>
                            <div
                              className="aga-utilizacao-bar"
                              aria-hidden
                            >
                              <span
                                className="aga-utilizacao-bar-fill aga-utilizacao-bar-fill--dese"
                                style={{
                                  width: `${Math.min(100, item.desejaveis.pct)}%`,
                                }}
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        </div>
      </div>

      <Modal
        show={modalConfirmar}
        onHide={() => !isConfirmando && setModalConfirmar(false)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton={!isConfirmando}>
          <Modal.Title>Confirmar geração do agendamento</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="aga-confirm-intro">
            Serão gerados com base no preview atual:
          </p>
          <ul className="aga-confirm-list">
            <li>
              <strong>{resumo?.total_propostas || 0}</strong> reunião(ões) de
              negócios
            </li>
            <li>
              <strong>{resumo?.total_caviar || 0}</strong> inscrição(ões) na
              degustação de caviar
            </li>
            <li>
              <strong>{resumo?.total_match_meet || 0}</strong> Match &amp; Meet
            </li>
          </ul>
          <div className="aga-confirm-alert" role="alert">
            <strong>Atenção:</strong> antes de gravar, o sistema vai excluir
            todas as reuniões de negócios e M&amp;M existentes e limpar as
            relações do caviar. Também reabre as escolhas que estavam com status
            “confirmado” (marcadas na geração anterior), para que voltem a
            entrar no matching. Em seguida o algoritmo roda de novo e cria as
            novas agendas. Toda essa movimentação é registrada nos logs.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="apl-btn apl-btn--ghost"
            onClick={() => setModalConfirmar(false)}
            disabled={isConfirmando}
          >
            <FaTimes /> Cancelar
          </button>
          <button
            type="button"
            className="apl-btn"
            onClick={confirmarAgendamento}
            disabled={isConfirmando}
          >
            {isConfirmando ? (
              <FaSpinner className="aga-spin" />
            ) : (
              <FaCheck />
            )}
            Confirmar e gravar
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!analiseModal}
        onHide={() => setAnaliseModal(null)}
        size="lg"
        centered
        className="aga-analise-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {analiseModal?.tipo === "patrocinador"
              ? `Memória do patrocinador — ${analiseModal?.nome || ""}`
              : `Memória do agendamento — ${analiseModal?.nome || "Participante"}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {analiseModal && (
            <>
              <div className="aga-analise-resumo">
                <span>
                  {analiseModal.tipo === "patrocinador"
                    ? "Reuniões (cota): "
                    : "Reuniões finais: "}
                  <strong>
                    {analiseModal.tipo === "patrocinador" &&
                    analiseModal.analise?.cota != null
                      ? `${analiseModal.analise?.reunioes_finais ?? 0}/${analiseModal.analise.cota}`
                      : (analiseModal.analise?.reunioes_finais ?? "—")}
                  </strong>
                </span>
                <span>
                  Eventos registrados:{" "}
                  <strong>
                    {analiseModal.analise?.eventos?.length || 0}
                  </strong>
                </span>
              </div>

              {analiseModal.analise?.eventos?.length ? (
                <ol className="aga-analise-timeline">
                  {analiseModal.analise.eventos.map((ev, idx) => (
                    <li
                      key={`${ev.id_patrocinador}-${ev.passo}-${idx}`}
                      className={
                        ev.resultado === "agendado"
                          ? "aga-analise-item is-ok"
                          : "aga-analise-item is-fail"
                      }
                    >
                      <div className="aga-analise-item-meta">
                        <span className="aga-analise-badge">
                          {ev.resultado === "agendado"
                            ? "Agendado"
                            : "Não agendado"}
                        </span>
                        <span>
                          Passo {ev.passo}
                          {ev.nome_plano ? ` · ${ev.nome_plano}` : ""}
                          {ev.lista === "caviar"
                            ? " · Caviar"
                            : ev.lista === "match_meet"
                              ? " · Match & Meet"
                              : ev.lista === "prioritaria"
                                ? " · Prioritária"
                                : ev.lista === "desejavel"
                                  ? " · Desejável"
                                  : ""}
                          {ev.sequencia_escolha != null
                            ? ` #${ev.sequencia_escolha}`
                            : ""}
                          {ev.id_slot != null ? ` · Slot ${ev.id_slot}` : ""}
                        </span>
                      </div>
                      <p>{ev.motivo}</p>
                    </li>
                  ))}
                </ol>
              ) : (
                <div className="apl-empty">
                  {analiseModal.tipo === "patrocinador"
                    ? "Não há memória de matching para este patrocinador neste preview."
                    : "Não há memória de matching para este participante neste preview (pode não ter entrado nas listas avaliadas ou o preview precisa ser gerado novamente)."}
                </div>
              )}

              {slots.length > 0 && (
                <div className="aga-analise-slots">
                  <div className="aga-analise-slots-head">
                    <h3>
                      {analiseModal.tipo === "patrocinador"
                        ? "Agenda do patrocinador"
                        : "Agenda do preview"}
                    </h3>
                    <span className="aga-analise-slots-hint">
                      {
                        slots.filter(
                          (s) => analiseModal.slots?.[`slot_${s.id}`],
                        ).length
                      }{" "}
                      de {slots.length} slots ocupados
                    </span>
                  </div>
                  <div className="aga-analise-slots-grid">
                    {slots.map((s) => {
                      const celula = analiseModal.slots?.[`slot_${s.id}`];
                      const tipo = celula?.tipo || null;
                      const hora = String(s.horario_inicio || "").substring(
                        0,
                        5,
                      );
                      return (
                        <article
                          key={s.id}
                          className={`aga-analise-slot-card${
                            celula
                              ? ` is-${tipo || "outro"}`
                              : " is-livre"
                          }`}
                        >
                          <header>
                            <span className="aga-analise-slot-id">
                              Slot {s.id}
                            </span>
                            {hora ? (
                              <span className="aga-analise-slot-hora">
                                {hora}
                              </span>
                            ) : null}
                          </header>
                          <p
                            className="aga-analise-slot-label"
                            title={celulaTitle(celula) || "Livre"}
                          >
                            {celulaLabel(celula) || "Livre"}
                          </p>
                          <span className="aga-analise-slot-tipo">
                            {!celula
                              ? "Disponível"
                              : tipo === "m_m"
                                ? "Match & Meet"
                                : tipo === "caviar"
                                  ? "Caviar"
                                  : tipo === "reuniao"
                                    ? "Reunião"
                                    : tipo === "conteudo"
                                      ? "Conteúdo"
                                      : tipo === "bloqueado"
                                        ? "Indisponível"
                                        : "Compromisso"}
                          </span>
                        </article>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="apl-btn apl-btn--ghost"
            onClick={() => setAnaliseModal(null)}
          >
            <FaTimes /> Fechar
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={modalCaviarSemSlot}
        onHide={() => setModalCaviarSemSlot(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Caviar — interessados sem slot disponível
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="aga-caviar-modal-intro">
            Convidados com interesse na ação especial que não puderam ser
            alocados nos slots 5 ou 6 (agenda ocupada, bloqueio ou vagas
            esgotadas).
          </p>
          {(resumo?.caviar_sem_slot || []).length === 0 ? (
            <div className="apl-empty">
              Nenhum interessado ficou sem slot neste preview.
            </div>
          ) : (
            <ol className="aga-caviar-sem-slot-list">
              {(resumo?.caviar_sem_slot || []).map((item) => (
                <li key={item.id_usuario}>
                  <strong>{item.nome}</strong>
                  <span>{item.motivo}</span>
                </li>
              ))}
            </ol>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="apl-btn apl-btn--ghost"
            onClick={() => setModalCaviarSemSlot(false)}
          >
            <FaTimes /> Fechar
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={modalMatchMeetSemSlot}
        onHide={() => setModalMatchMeetSemSlot(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Match & Meet — sem alocação
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="aga-caviar-modal-intro">
            Convidados com empresas no Match & Meet que não puderam ser
            marcados no slot 10 (agenda ocupada, patrocinador já com M&amp;M
            ou sem slot livre). Meta: 20 reuniões no slot 10.
          </p>
          {(resumo?.match_meet_sem_slot || []).length === 0 ? (
            <div className="apl-empty">
              Nenhum interessado ficou sem alocação neste preview.
            </div>
          ) : (
            <ol className="aga-caviar-sem-slot-list">
              {(resumo?.match_meet_sem_slot || []).map((item) => (
                <li key={item.id_usuario}>
                  <strong>{item.nome}</strong>
                  {(item.empresas || []).length > 0 && (
                    <span className="aga-mm-empresas">
                      Empresas: {(item.empresas || []).join(" · ")}
                    </span>
                  )}
                  <span>{item.motivo}</span>
                </li>
              ))}
            </ol>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="apl-btn apl-btn--ghost"
            onClick={() => setModalMatchMeetSemSlot(false)}
          >
            <FaTimes /> Fechar
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdmGerarAgendamento;
